import * as React from 'react';
import { useParams } from 'react-router-dom';
import { fieldValidation } from '../../../../../amorg-common';
import { incidentClientApi } from '../../../../../api/incidentClientApi';
import { LoadingPageOverlay } from '../../../../../designSystem/LoadingPageOverlay';
import { MessageCard } from '../../../../../designSystem/Message';
import { PageHeader } from '../../../../../designSystem/PageHeader';
import { Button } from '../../../../../designSystem/button/Button';
import { Flex } from '../../../../../designSystem/compositeComponents/FlexHelpers';
import { TextAreaField } from '../../../../../designSystem/formField/textArea/TextAreaField';
import { SingleColumnLayout } from '../../../../_layouts/SingleColumnLayout';
import { P } from '../../../../../designSystem/typography/Body';
import { PublicPageWrapper } from '../../../../_pageWrappers/PublicPageWrapper';


export function IncidentReporterPublicPage(): JSX.Element
{
    const { siteId = '', id = '' } = useParams<{ siteId: string; id: string }>();

    const [message, setMessage] = React.useState('');
    const [submitResult, setSubmitResult] = React.useState<'pending' | 'input-error' | 'server-error' | 'server-error-incident-resolved' | 'success'>('pending');
    const [loading, setLoading] = React.useState(false);

    const hasMessageError = !fieldValidation.incident.message(message.trim());

    async function handleSubmit(): Promise<void>
    {
        if (hasMessageError)
        {
            setSubmitResult('input-error');
            return;
        }

        setLoading(true);
        const result = await incidentClientApi.sendIncidentPublicReporterMessage(siteId, id, message.trim());
        setLoading(false);

        if (result.isSuccess)
        {
            setSubmitResult('success');
            setMessage('');
            return;
        }

        if (result.errorMessage === 'sendMessageToIncidentResponders_incident_resolved')
        {
            setSubmitResult('server-error-incident-resolved');
            return;
        }

        setSubmitResult('server-error');
    }

    return (
        <PublicPageWrapper siteId={siteId}>
            <PageHeader
                narrow
                headerStyle='secondary-gray'
                headerText='Skriv besked om din indberetning'
            />

            <SingleColumnLayout narrow>
                <Flex flexDirection='column' gap={16}>
                    <LoadingPageOverlay loading={loading} />

                    <P>
                        Brug formularen herunder til at sende en besked til din arbejdsmiljøorganisation vedrørende din indberetning.
                    </P>

                    {submitResult === 'input-error' &&
                        <MessageCard variant='error'>
                            <P paddingTop>Din besked kan ikke være tom.</P>
                        </MessageCard>
                    }

                    {submitResult === 'server-error' &&
                        <MessageCard variant='error'>
                            <P paddingTop>Din besked kunne ikke sendes. Prøv igen.</P>
                        </MessageCard>
                    }

                    {submitResult === 'server-error-incident-resolved' &&
                        <MessageCard variant='warning'>
                            <P paddingTop>Behandling af indberetningen er afsluttet, og der kan ikke længere sendes beskeder.</P>
                        </MessageCard>
                    }

                    {submitResult === 'success' &&
                        <MessageCard variant='success'>
                            <P paddingTop>Tak. Din besked er sendt til din arbejdsmiljøorganisation.</P>
                        </MessageCard>
                    }

                    <TextAreaField
                        label='Besked'
                        value={message}
                        minRows={8}
                        placeholder='Skriv din besked her'
                        disabled={submitResult === 'success' || submitResult === 'server-error-incident-resolved'}
                        errorMessage={submitResult === 'input-error' && hasMessageError ? 'Din besked kan ikke være tom.' : ''}
                        onChange={(value) => {
                            setMessage(value);
                            if (submitResult !== 'pending' && submitResult !== 'server-error-incident-resolved')
                                setSubmitResult('pending');
                        }}
                    />

                    <Flex justifyContent='flex-end'>
                        <Button
                            variant='filled'
                            text='Send besked'
                            onClick={() => handleSubmit()}
                            disabled={submitResult === 'success' || submitResult === 'server-error-incident-resolved'}
                        />
                    </Flex>
                </Flex>
            </SingleColumnLayout>
        </PublicPageWrapper>
    );
}
